import React, { useState } from "react";
import axios from "axios";
import { auth } from "../firebase";

const API_BASE = process.env.REACT_APP_PAYMENT_API_BASE || "http://localhost:5001/api";
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

// Academic year suggestion, e.g. "2026-27". Indian academic years start in June.
function currentAcademicYear(now = new Date()) {
  const start = now.getMonth() >= 5 ? now.getFullYear() : now.getFullYear() - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
}

const COPY = {
  student: {
    title: "Student verification",
    sub: "Upload your school or educational institution ID card. It must be for the current academic year.",
    fileLabel: "ID card (photo or PDF)",
  },
  senior: {
    title: "Senior Citizen verification",
    sub: "Upload your Aadhaar Card or any valid government-issued ID. Your date of birth or age must be clearly visible.",
    fileLabel: "Government ID (photo or PDF)",
  },
};

export default function VerificationModal({ category, onClose, onSubmitted }) {
  const copy = COPY[category];
  const [institution, setInstitution] = useState("");
  const [academicYear, setAcademicYear] = useState(currentAcademicYear());
  const [dob, setDob] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setError("");
    if (f && f.size > MAX_BYTES) {
      setFile(null);
      e.target.value = "";
      setError("That file is larger than 5 MB. Please upload a smaller photo or PDF.");
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) {
      setError("Please choose your document to upload.");
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setError("Please sign in again.");
      return;
    }
    const form = new FormData();
    form.append("category", category);
    if (category === "student") {
      form.append("institution", institution);
      form.append("academicYear", academicYear);
    } else {
      form.append("dob", dob);
    }
    form.append("document", file);

    setLoading(true);
    try {
      const token = await user.getIdToken();
      await axios.post(`${API_BASE}/payment/verification`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSubmitted();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not upload your document. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && !loading && onClose()}>
      <div className="modal-card">
        <button className="modal-close" onClick={onClose} aria-label="Close" disabled={loading}>✕</button>
        <h2>{copy.title}</h2>
        <p className="modal-sub">{copy.sub}</p>

        {error && <div className="error-msg">{error}</div>}

        <form onSubmit={submit}>
          {category === "student" ? (
            <>
              <div className="form-group">
                <label htmlFor="v-inst">School / institution</label>
                <input
                  id="v-inst" type="text" required maxLength={120}
                  placeholder="Name of your school or college"
                  value={institution} onChange={(e) => setInstitution(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="v-year">Academic year</label>
                <input
                  id="v-year" type="text" required maxLength={20}
                  placeholder="e.g. 2026-27"
                  value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}
                />
              </div>
            </>
          ) : (
            <div className="form-group">
              <label htmlFor="v-dob">Date of birth</label>
              <input
                id="v-dob" type="date" required
                max={new Date().toISOString().slice(0, 10)}
                value={dob} onChange={(e) => setDob(e.target.value)}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="v-file">{copy.fileLabel}</label>
            <input id="v-file" type="file" required accept={ACCEPT} onChange={onFile} />
            <p className="field-hint">JPG, PNG, WebP or PDF, up to 5 MB. Only our review team can see it.</p>
          </div>

          <button className="modal-btn-full" type="submit" disabled={loading}>
            {loading ? <span className="spinner" /> : "Submit for review"}
          </button>
        </form>
      </div>
    </div>
  );
}
